/**
 * PostRequirement.jsx — Buyer posts a new cybersecurity requirement.
 *
 * Fields: category, budget max (required), geo preference, certifications (multi-select),
 *         availability window, experience level, engagement type, timeline (weeks),
 *         optional scope doc upload.
 * On success: redirects to /buyer/requirements/:id/bids.
 *
 * Requirements: 23.3
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { apiClient } from '../../utils/auth';

const CATEGORIES = [
  'Penetration Testing',
  'Vulnerability Assessment',
  'Security Audit',
  'Incident Response',
  'Cloud Security',
  'Network Security',
  'Application Security',
  'Compliance & Risk',
];

const CERTIFICATIONS = ['CISSP', 'CEH', 'OSCP', 'CISM', 'ISO27001', 'PCI-DSS', 'SOC2'];

const EXPERIENCE_LEVELS = [
  { value: 'junior', label: 'Junior (0–3 years)' },
  { value: 'mid', label: 'Mid-level (3–7 years)' },
  { value: 'senior', label: 'Senior (7+ years)' },
];

const ENGAGEMENT_TYPES = [
  { value: 'ONE_TO_ONE', label: 'One to One' },
  { value: 'MULTI_BID',  label: 'Multi Bid' },
];
const TIMELINE_WEEKS = Array.from({ length: 20 }, (_, i) => i + 1);

export default function PostRequirement() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    category: '',
    budget_max: '',
    geo_preference: '',
    required_certifications: [],
    availability_window: '',
    experience_level: 'senior',
    engagement_type: 'MULTI_BID',
    timeline_weeks: '',
  });
  const [scopeDoc, setScopeDoc] = useState(null);
  const [scopeDocName, setScopeDocName] = useState('');
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
    setApiError('');
  };

  const handleCertChange = (e) => {
    const { value } = e.target;
    setForm((prev) => ({
      ...prev,
      required_certifications: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleScopeDoc = (e) => {
    const f = e.target.files[0];
    if (f) {
      setScopeDoc(f);
      setScopeDocName(f.name);
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.category) errs.category = 'Category is required.';
    if (!form.budget_max) errs.budget_max = 'Maximum budget is required.';
    if (!form.geo_preference) errs.geo_preference = 'Geo preference is required.';
    if (!form.availability_window) errs.availability_window = 'Availability window is required.';
    if (!form.timeline_weeks) errs.timeline_weeks = 'Timeline duration in weeks is required.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setLoading(true);
    setApiError('');

    try {
      let payload;
      let headers = {};

      if (scopeDoc) {
        payload = new FormData();
        Object.entries(form).forEach(([k, v]) => {
          if (Array.isArray(v)) {
            v.forEach((item) => payload.append(k, item));
          } else {
            payload.append(k, v);
          }
        });
        payload.append('scope_doc', scopeDoc);
        // Do NOT set Content-Type manually — browser sets it with the correct boundary
        headers = {};
      } else {
        payload = { ...form };
      }

      const { data } = await apiClient.post('/api/v1/requirements/', payload, { headers });
      navigate(`/buyer/requirements/${data.id}/bids`);
    } catch (err) {
      const msg = err.response?.data?.error?.message;
      setApiError(msg || 'Failed to post requirement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ maxWidth: 640, mx: 'auto', mt: 4, p: 3 }}
      noValidate
    >
      <Button
        variant="outlined"
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/buyer/requirements')}
        sx={{
          mb: 3,
          borderColor: 'rgba(99, 102, 241, 0.4)',
          color: '#818cf8',
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': {
            borderColor: '#6366f1',
            backgroundColor: 'rgba(99, 102, 241, 0.08)',
          }
        }}
      >
        Back to Requirements
      </Button>

      <Typography variant="h4" gutterBottom>
        Post a Requirement
      </Typography>

      <FormControl fullWidth margin="normal" error={!!errors.category}>
        <InputLabel id="category-label">Category *</InputLabel>
        <Select
          labelId="category-label"
          name="category"
          value={form.category}
          label="Category *"
          onChange={handleChange}
          inputProps={{ 'aria-label': 'Requirement category' }}
        >
          {CATEGORIES.map((c) => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </Select>
        {errors.category && (
          <Typography variant="caption" color="error">{errors.category}</Typography>
        )}
      </FormControl>

      <TextField
        label="Budget Max (Rs.)"
        name="budget_max"
        type="number"
        value={form.budget_max}
        onChange={handleChange}
        error={!!errors.budget_max}
        helperText={errors.budget_max}
        fullWidth
        required
        margin="normal"
        inputProps={{ min: 0, 'aria-label': 'Maximum budget' }}
      />

      <TextField
        label="Geo Preference"
        name="geo_preference"
        value={form.geo_preference}
        onChange={handleChange}
        error={!!errors.geo_preference}
        helperText={errors.geo_preference || 'e.g. India, Remote, Pan-India'}
        fullWidth
        required
        margin="normal"
        inputProps={{ 'aria-label': 'Geographic preference' }}
      />

      <FormControl fullWidth margin="normal">
        <InputLabel id="certs-label">Required Certifications</InputLabel>
        <Select
          labelId="certs-label"
          multiple
          value={form.required_certifications}
          onChange={handleCertChange}
          input={<OutlinedInput label="Required Certifications" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((v) => <Chip key={v} label={v} size="small" />)}
            </Box>
          )}
          inputProps={{ 'aria-label': 'Required certifications' }}
        >
          {CERTIFICATIONS.map((c) => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Availability Window"
        name="availability_window"
        value={form.availability_window}
        onChange={handleChange}
        error={!!errors.availability_window}
        helperText={errors.availability_window || 'e.g. immediate, Q2 2025'}
        fullWidth
        required
        margin="normal"
        inputProps={{ 'aria-label': 'Availability window' }}
      />

      <FormControl fullWidth margin="normal">
        <InputLabel id="exp-label">Experience Level</InputLabel>
        <Select
          labelId="exp-label"
          name="experience_level"
          value={form.experience_level}
          label="Experience Level"
          onChange={handleChange}
          inputProps={{ 'aria-label': 'Experience level' }}
        >
          {EXPERIENCE_LEVELS.map((e) => (
            <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <InputLabel id="eng-label">Engagement Type</InputLabel>
        <Select
          labelId="eng-label"
          name="engagement_type"
          value={form.engagement_type}
          label="Engagement Type"
          onChange={handleChange}
          inputProps={{ 'aria-label': 'Engagement type' }}
        >
          {ENGAGEMENT_TYPES.map((e) => (
            <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal" error={!!errors.timeline_weeks}>
        <InputLabel id="timeline-weeks-label">Timeline (Weeks) *</InputLabel>
        <Select
          labelId="timeline-weeks-label"
          name="timeline_weeks"
          value={form.timeline_weeks}
          label="Timeline (Weeks) *"
          onChange={handleChange}
          inputProps={{ 'aria-label': 'Timeline in weeks' }}
        >
          {TIMELINE_WEEKS.map((w) => (
            <MenuItem key={w} value={w}>
              {w} week{w > 1 ? 's' : ''}
            </MenuItem>
          ))}
        </Select>
        {errors.timeline_weeks && (
          <Typography variant="caption" color="error">{errors.timeline_weeks}</Typography>
        )}
      </FormControl>

      <Box sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          component="label"
          aria-label="Upload scope document"
        >
          {scopeDocName || 'Attach Scope Document (optional)'}
          <input
            type="file"
            hidden
            accept=".pdf,.docx"
            onChange={handleScopeDoc}
            aria-label="Scope document file"
          />
        </Button>
      </Box>

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
        sx={{ mt: 3 }}
        aria-label="Post requirement"
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Post Requirement'}
      </Button>
      {apiError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {apiError}
        </Alert>
      )}
    </Box>
  );
}
